import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { COUNTRIES, CUT_TYPES, FUEL_TYPES } from '../data/constants';
import { PART_TYPES } from '../data/partTypes';
import { useLanguage } from '../translations/LanguageContext';
import { PlusIcon, CheckIcon, DeleteIcon, UploadIcon } from './Icons';
import generateBrandedQR from '../utils/generateBrandedQR';

const VEHICLE_CLASSES = [
  { id: 'car', label: 'Car' },
  { id: 'truck', label: 'Truck' },
  { id: 'bus', label: 'Bus' },
  { id: 'machinery', label: 'Machinery' },
];
const _ptIds = new Set(PART_TYPES.map(pt => pt.id));

function BulkPartsUpload({ user, onDone }) {
  const { t } = useLanguage();
  const [commonImages, setCommonImages] = useState([]);
  const [common, setCommon] = useState({ make: '', model: '', year: '', chassis: '', condition: 'Used', country: '', yard_location: '', supplier: '', vehicleClass: 'car' });
  const [parts, setParts] = useState([{ partType: '', description: '', quantity: 1, price: '', currency: 'JPY', images: [] }]);
  const [customType, setCustomType] = useState('');
  const [showCustom, setShowCustom] = useState(null); // index of part showing custom input
  const [bulkMode, setBulkMode] = useState('single'); // 'single' = one unit with qty, 'separate' = individual units
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const commonFileRef = useRef();

  const setC = (k, v) => setCommon(p => ({ ...p, [k]: v }));

  const updatePart = (idx, key, val) => {
    setParts(prev => prev.map((p, i) => i === idx ? { ...p, [key]: val } : p));
  };

  const addPart = () => {
    setParts(prev => [...prev, { partType: '', description: '', quantity: 1, price: '', currency: 'JPY', images: [] }]);
  };

  const removePart = (idx) => {
    if (parts.length <= 1) return;
    setParts(prev => prev.filter((_, i) => i !== idx));
  };

  const handleCommonImages = (files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        uploadImage(ev.target.result).then(url => {
          setCommonImages(prev => [...prev, url || ev.target.result]);
        }).catch(() => setCommonImages(prev => [...prev, ev.target.result]));
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePartImages = (idx, files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        uploadImage(ev.target.result).then(url => {
          updatePart(idx, 'images', [...(parts[idx].images || []), url || ev.target.result]);
        }).catch(() => updatePart(idx, 'images', [...(parts[idx].images || []), ev.target.result]));
      };
      reader.readAsDataURL(file);
    });
  };

  const totalUnits = parts.reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);

  const filteredTypes = PART_TYPES.filter(pt => pt.vehicleClasses.includes(common.vehicleClass));
  const filteredGroups = [...new Set(filteredTypes.map(pt => pt.group))];

  const handleSubmit = async () => {
    // Validate
    const invalid = parts.find(p => !p.partType);
    if (invalid) { alert('Each part needs a part type.'); return; }

    setSubmitting(true);
    const partsList = DB.get('spareParts') || [];
    const created = [];
    const makeCode = (common.make || 'UNK').substring(0, 3).toUpperCase();
    const d = new Date();
    const dateCode = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;

    for (const part of parts) {
      const qty = parseInt(part.quantity) || 1;
      const partImages = part.images.length > 0 ? part.images : commonImages;
      const ptEntry = PART_TYPES.find(p => p.id === part.partType);
      const partTypeId = ptEntry ? ptEntry.id : 'other';
      const partTypeDisplay = ptEntry ? ptEntry.display : (part.partType || 'Other');
      const baseName = `${common.make || ''} ${common.model || ''} — ${partTypeDisplay}`.trim();

      if (bulkMode === 'single' || qty === 1) {
        // ONE unit with quantity field (e.g., 1 card showing "×12")
        const allParts = DB.get('spareParts') || [];
        const newId = DB.genId('PRT');
        const newSku = `PRT-${makeCode}-${dateCode}-${String(allParts.length + 1).padStart(3, '0')}`;
        const newPart = {
          id: newId, sku: newSku, partName: baseName,
          partType: partTypeId, category: partTypeDisplay, vehicleClass: common.vehicleClass,
          description: part.description, condition: common.condition || 'Used',
          make: common.make, model: common.model, year: common.year ? parseInt(common.year) : null,
          chassis: common.chassis, fitment: common.chassis,
          country: common.country, yard_location: common.yard_location || (common.country === 'UAE' ? 'sharjah_yard' : 'japan_yard'),
          images: partImages, auction_sheets: [],
          purchase_supplier: common.supplier,
          costJPY: part.price ? parseFloat(part.price) : null,
          purchase_currency: part.currency,
          quantity: qty, sold_quantity: 0, status: 'available',
          daysInStock: 0, createdAt: new Date().toISOString().split('T')[0],
        };
        DB.set('spareParts', [...(DB.get('spareParts') || []), newPart]);
        try {
          const qrDataUrl = await generateBrandedQR(`${window.location.origin}/?u=${newSku}`, { size: 472 });
          const qrUrl = await uploadImage(qrDataUrl);
          if (qrUrl) {
            const list = DB.get('spareParts') || [];
            DB.set('spareParts', list.map(u => u.id === newId ? { ...u, qr_code_url: qrUrl } : u));
          }
        } catch {}
        created.push({ sku: newSku, name: `${partTypeDisplay}${qty > 1 ? ` (×${qty} as one unit)` : ''}` });
      } else {
        // SEPARATE units — one card per item
        for (let q = 0; q < qty; q++) {
          const allParts = DB.get('spareParts') || [];
          const newId = DB.genId('PRT');
          const newSku = `PRT-${makeCode}-${dateCode}-${String(allParts.length + 1).padStart(3, '0')}`;
          const newPart = {
            id: newId, sku: newSku, partName: baseName,
            partType: partTypeId, category: partTypeDisplay, vehicleClass: common.vehicleClass,
            description: part.description, condition: common.condition || 'Used',
            make: common.make, model: common.model, year: common.year ? parseInt(common.year) : null,
            chassis: common.chassis, fitment: common.chassis,
            country: common.country, yard_location: common.yard_location || (common.country === 'UAE' ? 'sharjah_yard' : 'japan_yard'),
            images: partImages, auction_sheets: [],
            purchase_supplier: common.supplier,
            costJPY: part.price ? parseFloat(part.price) : null,
            purchase_currency: part.currency,
            quantity: 1, status: 'available',
            daysInStock: 0, createdAt: new Date().toISOString().split('T')[0],
          };
          DB.set('spareParts', [...(DB.get('spareParts') || []), newPart]);
          try {
            const qrDataUrl = await generateBrandedQR(`${window.location.origin}/?u=${newSku}`, { size: 472 });
            const qrUrl = await uploadImage(qrDataUrl);
            if (qrUrl) {
              const list = DB.get('spareParts') || [];
              DB.set('spareParts', list.map(u => u.id === newId ? { ...u, qr_code_url: qrUrl } : u));
            }
          } catch {}
          created.push({ sku: newSku, name: `${partTypeDisplay} (${q + 1} of ${qty})` });
        }
      }
    }

    setResults(created);
    setSubmitting(false);
  };

  // Success screen
  if (results) {
    return (
      <div>
        <div style={{ textAlign: 'center', padding: '40px 0 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', marginBottom: 8 }}>{results.length} parts added to inventory</h2>
        </div>
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-body">
            {results.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontWeight: 600 }}>{r.sku}</span>
                <span style={{ color: 'var(--text2)' }}>— {r.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => { setResults(null); setParts([{ partType: '', description: '', quantity: 1, price: '', currency: 'JPY', images: [] }]); setCommonImages([]); }}>Add More</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', marginBottom: 4 }}>Bulk Parts Upload</h1>
      <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>Quickly add multiple parts from the same vehicle at once.</p>

      {/* ── Common Photos ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Common Photos</div></div>
        <div className="card-body">
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Upload once — applies to all parts below unless overridden.</div>
          <div className="upload-zone" style={{ padding: '14px 12px', marginBottom: 8 }} onClick={() => commonFileRef.current?.click()}>
            <UploadIcon />
            <div style={{ fontSize: 12, marginTop: 4, color: 'var(--text2)' }}>Click to upload photos</div>
            <input ref={commonFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleCommonImages(e.target.files)} />
          </div>
          {commonImages.length > 0 && (
            <div className="upload-preview">
              {commonImages.map((src, i) => (
                <div key={i} className="upload-thumb" style={{ position: 'relative' }}>
                  <img src={src} alt="" />
                  <button type="button" className="upload-thumb-rm" onClick={() => setCommonImages(prev => prev.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Common Details ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><div className="card-title">Common Details</div></div>
        <div className="card-body">
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Source vehicle details — applies to all parts.</div>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Vehicle Class</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {VEHICLE_CLASSES.map(vc => (
                <button key={vc.id} type="button" onClick={() => setC('vehicleClass', vc.id)}
                  style={{
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: common.vehicleClass === vc.id ? '2px solid var(--blue)' : '1px solid var(--border)',
                    background: common.vehicleClass === vc.id ? 'rgba(37,99,235,0.1)' : 'var(--bg2)',
                    color: common.vehicleClass === vc.id ? 'var(--blue)' : 'var(--text2)',
                  }}>{vc.label}</button>
              ))}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Make</label><input className="form-input" value={common.make} onChange={e => setC('make', e.target.value)} placeholder="Toyota" /></div>
            <div className="form-group"><label className="form-label">Model</label><input className="form-input" value={common.model} onChange={e => setC('model', e.target.value)} placeholder="Hilux" /></div>
            <div className="form-group"><label className="form-label">Year</label><input className="form-input" type="number" value={common.year} onChange={e => setC('year', e.target.value)} placeholder="2018" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Chassis</label><input className="form-input" value={common.chassis} onChange={e => setC('chassis', e.target.value)} placeholder="Optional" /></div>
            <div className="form-group">
              <label className="form-label">Condition</label>
              <select className="form-select" value={common.condition} onChange={e => setC('condition', e.target.value)}>
                <option value="Used">Used</option><option value="New">New</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-select" value={common.country} onChange={e => { setC('country', e.target.value); setC('yard_location', e.target.value === 'UAE' ? 'sharjah_yard' : 'japan_yard'); }}>
                <option value="">Select</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Supplier</label><input className="form-input" value={common.supplier} onChange={e => setC('supplier', e.target.value)} placeholder="Optional" /></div>
          </div>
        </div>
      </div>

      {/* ── Parts List ── */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Parts List</div>

      {parts.map((part, idx) => (
        <div key={idx} className="card" style={{ marginBottom: 10, border: '1px solid var(--border)' }}>
          <div className="card-body" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>Part {idx + 1}</span>
              {parts.length > 1 && (
                <button type="button" onClick={() => removePart(idx)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12 }}>
                  <DeleteIcon /> Remove
                </button>
              )}
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">Part Type <span className="req">*</span></label>
                {part.partType && !_ptIds.has(part.partType) ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Custom</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{part.partType}</span>
                    <button type="button" onClick={() => updatePart(idx, 'partType', '')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>✕</button>
                  </div>
                ) : showCustom === idx ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="form-input" value={customType} onChange={e => setCustomType(e.target.value)} placeholder="Custom part type" autoFocus />
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => { if (customType.trim()) { updatePart(idx, 'partType', customType.trim()); setCustomType(''); setShowCustom(null); } }}>✓</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowCustom(null); setCustomType(''); }}>✕</button>
                  </div>
                ) : (
                  <select className="form-select" value={part.partType} onChange={e => {
                    if (e.target.value === '__custom__') { setShowCustom(idx); setCustomType(''); }
                    else updatePart(idx, 'partType', e.target.value);
                  }}>
                    <option value="">Select type</option>
                    {filteredGroups.map(g => (
                      <optgroup key={g} label={g}>
                        {filteredTypes.filter(pt => pt.group === g).map(pt => (
                          <option key={pt.id} value={pt.id}>{pt.display}</option>
                        ))}
                      </optgroup>
                    ))}
                    <option value="__custom__">+ Custom</option>
                  </select>
                )}
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Qty</label>
                <input className="form-input" type="number" min="1" value={part.quantity} onChange={e => updatePart(idx, 'quantity', parseInt(e.target.value) || 1)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Price</label>
                <input className="form-input" type="number" value={part.price} onChange={e => updatePart(idx, 'price', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group" style={{ width: 80 }}>
                <label className="form-label">Currency</label>
                <select className="form-select" value={part.currency} onChange={e => updatePart(idx, 'currency', e.target.value)}>
                  <option>JPY</option><option>AED</option><option>USD</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-input" value={part.description} onChange={e => updatePart(idx, 'description', e.target.value)} placeholder="e.g. Front bumper, white, good condition" />
            </div>

            {/* Part-specific photos */}
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
              {part.images.length > 0 ? (
                <div>
                  <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ {part.images.length} specific photo{part.images.length !== 1 ? 's' : ''}</span>
                  <button type="button" onClick={() => updatePart(idx, 'images', [])} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 10 }}>Clear</button>
                </div>
              ) : (
                <label style={{ cursor: 'pointer', color: 'var(--blue)' }}>
                  📷 Add specific photos (overrides common)
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handlePartImages(idx, e.target.files)} />
                </label>
              )}
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="btn btn-secondary" onClick={addPart} style={{ marginBottom: 20, width: '100%' }}>
        <PlusIcon /> Add Another Part
      </button>

      {/* ── Quantity Mode Toggle ── */}
      {totalUnits > 1 && (
        <div style={{ padding: '12px 16px', background: 'var(--bg3)', borderRadius: 10, marginBottom: 12, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)', marginBottom: 8 }}>How should quantity be handled?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setBulkMode('single')}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: bulkMode === 'single' ? '2px solid var(--blue)' : '1px solid var(--border)',
                background: bulkMode === 'single' ? 'rgba(37,99,235,0.1)' : 'var(--bg2)',
                color: bulkMode === 'single' ? 'var(--blue)' : 'var(--text2)',
              }}
            >
              One unit card with quantity
              <div style={{ fontSize: 10, fontWeight: 400, marginTop: 4, opacity: 0.7 }}>
                e.g. 1 card showing "×{totalUnits}" — sell partial from it
              </div>
            </button>
            <button
              onClick={() => setBulkMode('separate')}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: bulkMode === 'separate' ? '2px solid var(--blue)' : '1px solid var(--border)',
                background: bulkMode === 'separate' ? 'rgba(37,99,235,0.1)' : 'var(--bg2)',
                color: bulkMode === 'separate' ? 'var(--blue)' : 'var(--text2)',
              }}
            >
              Separate unit cards
              <div style={{ fontSize: 10, fontWeight: 400, marginTop: 4, opacity: 0.7 }}>
                e.g. {totalUnits} individual cards — sell each separately
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ── Submit ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>
          {bulkMode === 'single'
            ? `${parts.length} part type${parts.length !== 1 ? 's' : ''} · ${totalUnits} total quantity`
            : `${totalUnits} separate unit${totalUnits !== 1 ? 's' : ''} will be created`
          }
        </span>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
          <CheckIcon /> {submitting ? 'Adding...' : bulkMode === 'single'
            ? `Add ${parts.length} Part${parts.length !== 1 ? 's' : ''}`
            : `Add ${totalUnits} Unit${totalUnits !== 1 ? 's' : ''}`
          }
        </button>
      </div>
    </div>
  );
}

export default BulkPartsUpload;
